import { useCallback, useEffect, useMemo, useState } from "react";
import {
  bulkSetManufacturingPricingLayerValues,
  listManufacturingPlants,
  upsertManufacturingPricingLayerValue,
} from "@/lib/manufacturingClient";
import {
  cogsRowWithColumnValue,
  cogsRowWithMaterialsSummary,
  filterCogsCatalogRows,
  recipeKindsForScope,
  type CogsGap,
  type CogsGapMatch,
  type CogsMaterialsPatch,
  type CogsSkuScope,
} from "@/lib/manufacturing/cogsCatalogView";
import {
  COGS_DEFAULT_PAGE_SIZE,
  isCogsPageSize,
  type CogsPageSize,
} from "@/lib/manufacturing/cogsPaging";
import type {
  MfgPlant,
  MfgPricingLayerBand,
  MfgPricingLayerGridRow,
} from "@/lib/manufacturing/types";
import { useCogsCatalogMemory } from "./useCogsCatalogMemory";
import { useCogsPageMaterialsCalc } from "./useCogsPageMaterialsCalc";

export type PricingGridFilters = {
  series: string[];
  roof_style: string[];
  size_band: string[];
  plant_id: string;
  include_material_uses: boolean;
  include_named_variants: boolean;
  gaps: CogsGap[];
  gap_match: CogsGapMatch;
  sku_scope: CogsSkuScope;
};

const EMPTY_FILTERS: PricingGridFilters = {
  series: [],
  roof_style: [],
  size_band: [],
  plant_id: "",
  include_material_uses: false,
  include_named_variants: false,
  gaps: [],
  gap_match: "and",
  sku_scope: "products",
};

function rowKey(row: Pick<MfgPricingLayerGridRow, "subject_kind" | "subject_id">): string {
  return `${row.subject_kind}:${row.subject_id}`;
}

export function useManufacturingPricingGrid(enabled: boolean, navTick: number) {
  const [filters, setFilters] = useState<PricingGridFilters>(EMPTY_FILTERS);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [bulkColumnId, setBulkColumnId] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [pageSize, setPageSizeState] = useState<CogsPageSize>(COGS_DEFAULT_PAGE_SIZE);
  const [pageOffset, setPageOffset] = useState(0);

  const catalog = useCogsCatalogMemory({
    enabled,
    navTick,
    plantId: filters.plant_id,
    includeMaterialUses: filters.include_material_uses,
    includeNamedVariants: filters.include_named_variants,
  });

  const reloadPlants = useCallback(async () => {
    if (!enabled) return;
    const r = await listManufacturingPlants();
    if (!r.ok) return;
    const active = r.data.plants.filter((p) => p.is_active !== false);
    setPlants(active);
    setFilters((prev) => {
      if (!prev.plant_id) return prev;
      if (active.some((p) => p.id === prev.plant_id)) return prev;
      return { ...prev, plant_id: "" };
    });
  }, [enabled]);

  useEffect(() => {
    void reloadPlants();
  }, [reloadPlants, navTick]);

  useEffect(() => {
    setPageOffset(0);
  }, [
    filters.include_material_uses,
    filters.include_named_variants,
    filters.plant_id,
    filters.gap_match,
    filters.gaps,
    filters.roof_style,
    filters.series,
    filters.size_band,
    filters.sku_scope,
  ]);

  useEffect(() => {
    setBulkColumnId((prev) => {
      if (prev && catalog.columns.some((column) => column.id === prev)) return prev;
      return catalog.columns[0]?.id ?? "";
    });
  }, [catalog.columns]);

  const filtered = useMemo(
    () =>
      filterCogsCatalogRows(catalog.rows, {
        series: filters.series,
        roof_style: filters.roof_style,
        size_band: filters.size_band,
        gaps: filters.gaps,
        gapMatch: filters.gap_match,
        recipeKinds: recipeKindsForScope(filters.sku_scope),
      }),
    [
      catalog.rows,
      filters.gap_match,
      filters.gaps,
      filters.roof_style,
      filters.series,
      filters.size_band,
      filters.sku_scope,
    ],
  );
  const rows = useMemo(
    () => filtered.slice(pageOffset, pageOffset + pageSize),
    [filtered, pageOffset, pageSize],
  );
  const totalCount = filtered.length;

  useEffect(() => {
    const allowed = new Set(filtered.map(rowKey));
    setSelectedKeys((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const key of prev) {
        if (allowed.has(key)) next.add(key);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filtered]);

  const columnsByBand = useMemo(() => {
    const groups: Record<MfgPricingLayerBand, typeof catalog.columns> = {
      cogs: [],
      wholesale: [],
      msrp: [],
    };
    for (const column of catalog.columns.filter((col) => col.is_active)) {
      groups[column.band].push(column);
    }
    for (const band of ["cogs", "wholesale", "msrp"] as const) {
      groups[band].sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key));
    }
    return groups;
  }, [catalog.columns]);

  const allVisibleSelected = rows.length > 0 && rows.every((row) => selectedKeys.has(rowKey(row)));
  const selectedCount = selectedKeys.size;

  const toggleRow = (row: MfgPricingLayerGridRow) => {
    const key = rowKey(row);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const row of rows) next.delete(rowKey(row));
      } else {
        for (const row of rows) next.add(rowKey(row));
      }
      return next;
    });
  };

  const patchColumnValue = useCallback(
    (subjects: Array<{ subject_kind: string; subject_id: string }>, columnId: string, value: number) => {
      const keys = new Set(subjects.map((subject) => `${subject.subject_kind}:${subject.subject_id}`));
      catalog.setRows((prev) =>
        prev.map((row) => {
          if (!keys.has(rowKey(row))) return row;
          return cogsRowWithColumnValue(row, catalog.columns, columnId, value) ?? row;
        }),
      );
    },
    [catalog],
  );

  const applySummaries = useCallback(
    (summaries: CogsMaterialsPatch[]) => {
      const byRecipe = new Map(summaries.map((summary) => [summary.recipe_id, summary]));
      catalog.setRows((prev) =>
        prev.map((row) => {
          if (row.subject_kind !== "recipe") return row;
          const summary = byRecipe.get(row.subject_id);
          if (!summary) return row;
          return cogsRowWithMaterialsSummary(row, summary, catalog.columns);
        }),
      );
    },
    [catalog],
  );

  const saveCell = async (
    row: MfgPricingLayerGridRow,
    columnId: string,
    raw: string,
  ): Promise<boolean> => {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setActionError("Enter a valid number.");
      return false;
    }
    setActionBusy(true);
    setActionError(null);
    const r = await upsertManufacturingPricingLayerValue({
      column_id: columnId,
      subject_kind: row.subject_kind,
      subject_id: row.subject_id,
      value_numeric: n,
      plant_id: filters.plant_id || undefined,
    });
    setActionBusy(false);
    if (!r.ok) {
      setActionError(r.error);
      return false;
    }
    patchColumnValue([row], columnId, n);
    return true;
  };

  const applyBulkValue = async () => {
    if (!bulkColumnId || selectedCount === 0) return;
    const n = Number(bulkValue);
    if (!Number.isFinite(n)) {
      setActionError("Enter a valid bulk value.");
      return;
    }
    const subjects = filtered
      .filter((row) => selectedKeys.has(rowKey(row)))
      .filter((row) => cogsRowWithColumnValue(row, catalog.columns, bulkColumnId, n) != null)
      .map((row) => ({ subject_kind: row.subject_kind, subject_id: row.subject_id }));
    if (subjects.length === 0) {
      setActionError("That column is filled from its source and can't be typed.");
      return;
    }
    setActionBusy(true);
    setActionError(null);
    const r = await bulkSetManufacturingPricingLayerValues({
      column_id: bulkColumnId,
      value_numeric: n,
      subjects,
      plant_id: filters.plant_id || undefined,
    });
    setActionBusy(false);
    if (!r.ok) {
      setActionError(r.error);
      return;
    }
    patchColumnValue(subjects, bulkColumnId, n);
  };

  const pageCalcApi = useCogsPageMaterialsCalc({
    enabled,
    listBusy: catalog.listBusy,
    rows,
    allRows: filtered,
    plantId: filters.plant_id,
    series: filters.series,
    roofStyle: filters.roof_style,
    sizeBand: filters.size_band,
    pageSize,
    pageOffset,
    totalCount,
    applySummaries,
    onError: setActionError,
  });

  const pageStart = totalCount === 0 ? 0 : pageOffset + 1;
  const pageEnd = Math.min(pageOffset + pageSize, totalCount);
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageIndex = Math.floor(pageOffset / pageSize);

  return {
    filters,
    setFilters,
    plants,
    columns: catalog.columns,
    setColumns: catalog.setColumns,
    columnsByBand,
    rows,
    catalogRows: catalog.rows,
    setCatalogRows: catalog.setRows,
    reportError: catalog.setLocalError,
    filteredRows: filtered,
    selectedKeys,
    listBusy: catalog.listBusy,
    actionBusy,
    localError: actionError ?? catalog.localError,
    bulkColumnId,
    setBulkColumnId,
    bulkValue,
    setBulkValue,
    distinctSeries: catalog.series,
    distinctRoofStyles: catalog.roofStyles,
    distinctSizeBands: catalog.sizeBands,
    allVisibleSelected,
    selectedCount,
    toggleRow,
    toggleSelectAllVisible,
    saveCell,
    applyBulkValue,
    refreshMaterials: pageCalcApi.calculateVisiblePage,
    rowKey,
    reloadGrid: catalog.reload,
    pageOffset,
    pageSize,
    setPageSize: (n: number) => {
      if (!isCogsPageSize(n) || n === pageSize) return;
      setPageSizeState(n);
      setPageOffset(0);
    },
    totalCount,
    pageStart,
    pageEnd,
    pageIndex,
    pageCount,
    pageCalc: pageCalcApi.pageCalc,
    backgroundFill: pageCalcApi.backgroundFill,
    continueBackgroundFill: pageCalcApi.continueBackground,
    goToPage: (index: number) => setPageOffset(Math.max(0, index) * pageSize),
  };
}

export type ManufacturingPricingGridState = ReturnType<typeof useManufacturingPricingGrid>;
